'use client';

import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { countryToIso } from '@sola/schema/dotcom/shared/geo.ts';
import { trackViewContentList } from '@sola/tracking/lib/analytics/events.ts';
import type { ContentFeedCardProps } from '../content-feed-card/content-feed-card.tsx';
import { ContentFeedCard } from '../content-feed-card/content-feed-card.tsx';
import { useContentFeed } from './content-feed.logic.ts';
import type { ContentFeedEntryResolved } from './content-feed.types.ts';

/**
 * Resolves a country display name from an ISO code using i18n geo translations.
 * Handles Compassion's non-standard codes (DR, ES, GU, etc.) via countryToIso.
 */
const resolveCountryName = (isoCode: string | undefined, t: ReturnType<typeof useTranslation>['t']): string | undefined => {
  if (!isoCode) {
    return undefined;
  }
  const normalized = countryToIso(isoCode);
  const translated = t(`geo:countries.${normalized}.nameS`);
  // If the key isn't found, i18next returns the key itself
  return translated.startsWith('geo:') ? undefined : translated;
};
const contentFeedContainerClass = 'ContentFeed py-comfy-lg max-w-md mx-auto flex flex-col gap-comfy-lg';
const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;

/**
 * Maps an API entry to ContentFeedCard props.
 */
const mapEntryToCardProps = (entry: ContentFeedEntryResolved, t: ReturnType<typeof useTranslation>['t']): ContentFeedCardProps => {
  const isNew = !!entry.publishedAt && Date.now() - new Date(entry.publishedAt).getTime() < sevenDaysMs;
  const props: ContentFeedCardProps = {
    sourceType: entry.source,
    title: entry.title || '',
    description: entry.description || undefined,
    media: entry.media,
    publishedAt: entry.publishedAt,
    author: entry.author,
    match: entry.match,
    entryParticipants: entry.participants,
    programCountryIsoCode: entry.programCountry?.isoCode,
    programCountryName: resolveCountryName(entry.programCountry?.isoCode, t),
    hasNewUpdate: isNew,
    reactions: entry.reactions,
    canReact: entry.canReact,
    canComment: entry.canComment,
    topComment: entry.topComment,
    commentCount: entry.commentCount
  };
  if (entry.source === 'causes' && entry.metadata) {
    if (entry.metadata.fundingProgress) {
      props.fundingProgress = entry.metadata.fundingProgress;
    }
    if (entry.metadata.link) {
      props.link = entry.metadata.link;
    }
  }
  return props;
};
export interface ContentFeedViewProps {
  /** Resolved feed entries to render. */
  entries: ContentFeedEntryResolved[];
  /** Whether the feed is loading (initial load or "load more"). */
  isLoading: boolean;
  /** Whether there are more entries to load. */
  hasMore: boolean;
  /** Whether the feed passes suppression thresholds and should render at all. */
  shouldRender: boolean;
  /** Map of participant localId → preferred name (for banner text resolution). */
  participantNameMap: Map<string, string>;
  /** Called when the user requests the next page of entries. */
  onLoadMore: () => void;
  /** Heading title for the feed section */
  title?: string;
  /** Subtitle/description below the heading */
  subtitle?: string;
}

/**
 * Presentational Content Feed. Prop-only and side-effect-light: renders the
 * skeleton, entry list, "load more" control, and end message from the props it
 * receives. All data fetching lives in the {@link ContentFeed} wrapper, which
 * keeps this component trivial to render in Storybook and tests.
 */
export const ContentFeedView: React.FC<ContentFeedViewProps> = t0 => {
  const $ = _c(44);
  const {
    entries,
    isLoading,
    hasMore,
    shouldRender,
    participantNameMap,
    onLoadMore,
    title,
    subtitle
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["my"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["geo"];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    t: tGeo
  } = useTranslation(t2);
  const prevEntryCountRef = useRef(0);
  let t3;
  let t4;
  if ($[2] !== entries) {
    t3 = () => {
      if (entries.length === 0 || entries.length === prevEntryCountRef.current) {
        return;
      }
      const newEntries = entries.slice(prevEntryCountRef.current);
      prevEntryCountRef.current = entries.length;
      trackViewContentList("content-feed", "My Compassion Content Feed", newEntries.map(_temp));
    };
    t4 = [entries];
    $[2] = entries;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  let t5;
  if ($[5] !== t || $[6] !== title) {
    t5 = title || t("my:contentFeed.heading");
    $[5] = t;
    $[6] = title;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const headingTitle = t5;
  let t6;
  if ($[8] !== subtitle || $[9] !== t) {
    t6 = subtitle || t("my:contentFeed.subtitle");
    $[8] = subtitle;
    $[9] = t;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const headingSubtitle = t6;
  if (isLoading && entries.length === 0) {
    let t7;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = cn(contentFeedContainerClass, "w-full");
      $[11] = t7;
    } else {
      t7 = $[11];
    }
    let t8;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <div className="flex flex-col w-full gap-snug-sm pt-comfy-xs pb-snug-xl"><Skeleton height={30} width="40%" borderRadius="var(--outline-radius-sm)" /><Skeleton height={48} borderRadius="var(--outline-radius-sm)" /></div>;
      $[12] = t8;
    } else {
      t8 = $[12];
    }
    let t9;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <div className={t7} aria-busy="true">{t8}<div className="flex flex-col gap-comfy-sm pb-comfy-sm"><Skeleton height={300} borderRadius="var(--outline-radius-lg)" /><Skeleton height={300} borderRadius="var(--outline-radius-lg)" /></div></div>;
      $[13] = t9;
    } else {
      t9 = $[13];
    }
    return t9;
  }
  if (!shouldRender) {
    return null;
  }
  let t7;
  if ($[14] !== headingTitle) {
    t7 = <h2 className="heading-sm heading-neutral-amplified">{headingTitle}</h2>;
    $[14] = headingTitle;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== headingSubtitle) {
    t8 = <p className="body-sm label-neutral-mild">{headingSubtitle}</p>;
    $[16] = headingSubtitle;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t7 || $[19] !== t8) {
    t9 = <div className="pt-comfy-xs pb-snug-xl">{t7}{t8}</div>;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== entries || $[22] !== participantNameMap || $[23] !== tGeo) {
    let t11;
    if ($[25] !== participantNameMap || $[26] !== tGeo) {
      t11 = entry_0 => <ContentFeedCard key={entry_0.id} entryId={entry_0.id} participantNameMap={participantNameMap} {...mapEntryToCardProps(entry_0, tGeo)} />;
      $[25] = participantNameMap;
      $[26] = tGeo;
      $[27] = t11;
    } else {
      t11 = $[27];
    }
    t10 = entries.map(t11);
    $[21] = entries;
    $[22] = participantNameMap;
    $[23] = tGeo;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[28] !== hasMore || $[29] !== isLoading || $[30] !== onLoadMore || $[31] !== t) {
    t11 = hasMore && <div className="flex justify-center py-comfy-md" aria-live="polite"><ButtonStyle variant="secondary" size="lg"><button type="button" onClick={onLoadMore} disabled={isLoading}>{isLoading ? t("my:contentFeed.loading") : t("my:contentFeed.loadMore")}</button></ButtonStyle></div>;
    $[28] = hasMore;
    $[29] = isLoading;
    $[30] = onLoadMore;
    $[31] = t;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== entries.length || $[34] !== hasMore || $[35] !== t) {
    t12 = !hasMore && entries.length > 0 && <div className="flex flex-col items-center text-center py-comfy-md gap-snug-sm"><p className="label-lg label-neutral-amplified font-bold">{t("my:contentFeed.endTitle")}</p><p className="body-sm label-neutral-moderate">{t("my:contentFeed.endMessage")}</p></div>;
    $[33] = entries.length;
    $[34] = hasMore;
    $[35] = t;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== t10 || $[38] !== t11 || $[39] !== t12) {
    t13 = <div className="flex flex-col gap-comfy-sm pb-comfy-sm">{t10}{t11}{t12}</div>;
    $[37] = t10;
    $[38] = t11;
    $[39] = t12;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  let t14;
  if ($[41] !== t13 || $[42] !== t9) {
    t14 = <div className={contentFeedContainerClass}>{t9}{t13}</div>;
    $[41] = t13;
    $[42] = t9;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  return t14;
};
export interface ContentFeedProps {
  /** Number of entries requested per feed request. */
  limit?: number;
  /** Heading title for the feed section */
  title?: string;
  /** Subtitle/description below the heading */
  subtitle?: string;
}

/**
 * Content Feed advanced block.
 * Displays a paginated feed of content entries (community, causes, compassion.media)
 * from the Supporter Global Content API.
 * Applies suppression rules to hide the feed when content is insufficient or stale.
 *
 * Smart wrapper: owns data fetching via {@link useContentFeed} and delegates all
 * rendering to {@link ContentFeedView}.
 */
export const ContentFeed: React.FC<ContentFeedProps> = t0 => {
  const $ = _c(9);
  const {
    title,
    subtitle,
    limit
  } = t0;
  const {
    entries,
    isLoading,
    hasMore,
    loadMore,
    shouldRender,
    participantNameMap
  } = useContentFeed(limit);
  let t1;
  if ($[0] !== entries || $[1] !== hasMore || $[2] !== isLoading || $[3] !== loadMore || $[4] !== participantNameMap || $[5] !== shouldRender || $[6] !== subtitle || $[7] !== title) {
    t1 = <ContentFeedView entries={entries} isLoading={isLoading} hasMore={hasMore} shouldRender={shouldRender} participantNameMap={participantNameMap} onLoadMore={loadMore} title={title} subtitle={subtitle} />;
    $[0] = entries;
    $[1] = hasMore;
    $[2] = isLoading;
    $[3] = loadMore;
    $[4] = participantNameMap;
    $[5] = shouldRender;
    $[6] = subtitle;
    $[7] = title;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  return t1;
};
function _temp(entry) {
  return {
    content_id: entry.id,
    content_type: entry.source,
    content_tags: [entry.source],
    content_title: entry.title || ""
  };
}